/**
 * @fileoverview Generic file viewer component for PDF, images, and videos
 * Detects file type from extension and renders appropriate viewer
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { intl, getToken, DmsButton } from "dmsbasereact";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { getBaseURL } from "../../build_config";
import { getFileType } from "./helpers";

/**
 * PDF Viewer component using react-pdf-viewer with authentication
 */
const PdfViewer = ({ fileUrl, token, height }) => {
    const defaultLayoutPluginInstance = defaultLayoutPlugin();
    return (
        <Box sx={{ height }}>
            <Worker workerUrl="/pdf.worker.min.js">
                <Viewer
                    fileUrl={fileUrl}
                    httpHeaders={{ Authorization: `Bearer ${token?.access_token}` }}
                    withCredentials={true}
                    plugins={[defaultLayoutPluginInstance]}
                    renderError={(error) => (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            flexDirection="column"
                            p={4}
                        >
                            <Typography color="error" gutterBottom>
                                {intl.get("ErrorLoadingDocument").d("Error loading document")}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {error.message}
                            </Typography>
                        </Box>
                    )}
                    renderLoader={(percentages) => (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            flexDirection="column"
                        >
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>
                                {intl.get("Loading").d("Loading...")} {Math.round(percentages)}%
                            </Typography>
                        </Box>
                    )}
                />
            </Worker>
        </Box>
    );
};

PdfViewer.propTypes = {
    fileUrl: PropTypes.string.isRequired,
    token: PropTypes.object,
    height: PropTypes.string
};

/**
 * Media Viewer component for images and videos
 * Uses fetch with authentication to load file as blob
 */
const MediaViewer = ({ filePk, fileType, height }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let objectUrl = null;

        const loadMedia = async () => {
            try {
                setLoading(true);
                setError(null);
                const fileUrl = `${getBaseURL()}/file/${filePk}`;
                const token = getToken();
                const response = await fetch(fileUrl, {
                    headers: { Authorization: `Bearer ${token?.access_token}` }
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                setBlobUrl(objectUrl);
            } catch (err) {
                console.error("Error loading media:", err);
                setError(intl.get("ErrorLoadingFile").d("Error loading file"));
            } finally {
                setLoading(false);
            }
        };

        if (filePk) {
            loadMedia();
        }

        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [filePk]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={height}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={height}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!blobUrl) {
        return null;
    }

    if (fileType === "image") {
        return (
            <Box
                sx={{
                    height,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#f5f5f5",
                    p: 2
                }}
            >
                <img
                    src={blobUrl}
                    alt="Document"
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
            </Box>
        );
    }

    if (fileType === "video") {
        return (
            <Box
                sx={{
                    height,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#000"
                }}
            >
                <video
                    src={blobUrl}
                    controls
                    style={{ maxWidth: "100%", maxHeight: "100%" }}
                />
            </Box>
        );
    }

    return null;
};

MediaViewer.propTypes = {
    filePk: PropTypes.number.isRequired,
    fileType: PropTypes.oneOf(["image", "video"]).isRequired,
    height: PropTypes.string
};

/**
 * Unknown File Viewer component for unsupported file types
 * Shows message and authenticated download button
 */
const UnknownFileViewer = ({ filePk, fileName, height }) => {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = useCallback(async () => {
        if (!filePk) return;

        try {
            setDownloading(true);
            const token = getToken();
            const response = await fetch(`${getBaseURL()}/file/${filePk}`, {
                headers: { Authorization: `Bearer ${token?.access_token}` }
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || `file_${filePk}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Download failed:", err);
        } finally {
            setDownloading(false);
        }
    }, [filePk, fileName]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height={height}
            gap={2}
        >
            <Typography color="textSecondary">
                {intl.get("PreviewNotAvailable").d("Preview not available for this file type")}
            </Typography>
            <DmsButton
                variant="primary"
                text={downloading ? intl.get("Downloading").d("Downloading...") : intl.get("Download").d("Download")}
                onClick={handleDownload}
                disabled={downloading}
            />
        </Box>
    );
};

UnknownFileViewer.propTypes = {
    filePk: PropTypes.number.isRequired,
    fileName: PropTypes.string,
    height: PropTypes.string
};

/**
 * DmsFileViewer - Generic file viewer component
 * Automatically detects file type and renders appropriate viewer
 */
export const DmsFileViewer = ({ filePk = null,
  fileName = "",
  height = "70vh"}) => {
    const fileType = getFileType(fileName);
    const fileUrl = `${getBaseURL()}/file/${filePk}`;
    const token = getToken();

    if (!filePk) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={height}>
                <Typography color="textSecondary">
                    {intl.get("NoFileSelected").d("No file selected")}
                </Typography>
            </Box>
        );
    }

    switch (fileType) {
        case "pdf":
            return <PdfViewer fileUrl={fileUrl} token={token} height={height} />;
        case "image":
        case "video":
            return <MediaViewer filePk={filePk} fileType={fileType} height={height} />;
        default:
            return <UnknownFileViewer filePk={filePk} fileName={fileName} height={height} />;
    }
};

DmsFileViewer.propTypes = {
    filePk: PropTypes.number,
    fileName: PropTypes.string,
    height: PropTypes.string
};

